import { useCallback, useEffect, useRef, useState } from "react";
import {
  BACK_IN_STOCK_BUTTON_ID,
  BACK_IN_STOCK_SELECTED_PRODUCT,
  BACK_IN_STOCK_SELECTED_VARIANT,
} from "../../constants";
import AnonymousBackInStockVariantModal from "./variants/AnonymousBackInStockVariantModal";
import {
  VARIANT_CONFIGS,
  DEFAULT_VARIANT_CONFIG,
} from "./variants/variantConfig";
import useEventBus from "../../hooks/useEventBus";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import {
  getAnonymousId,
  anonymousAddVariantToNotifications,
} from "../../api/backInStockApi";
import { AnonymousBackInStockModalParameters } from "../../types/components/anonymousBackInStock";
import { checkEmailStatus } from "./utils/checkEmailStatus";
import validateEmail from "./utils/validateEmail";
import getWindow from "../../utils/window";
import { loginRedirect } from "../../utils/identity";
import { createAnonymousBrazeUser } from "./utils/createAnonymousBrazeUser";
import { setSessionStorageItem } from "../../utils/storage";
import { useAnonymousBackInStock } from "../../context/anonymousBackInStock/AnonymousBackInStockContext";
import { ERROR_TYPE, logError } from "../../utils/newRelicLogger";
import {
  fireRegisteredAccountScreenLoadAnalytics,
  fireAnonymousBackInStockSuccessAnalytics,
} from "../../analytics/anonymousBackInStock";
import { getFeature, track } from "../../utils/features";
import { useFeaturesReady } from "../../context/featuresReady/FeaturesReadyContext";

const AnonymousBackInStockModal = (): JSX.Element | null => {
  const recognisedHeadingRef = useRef<HTMLHeadingElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const wasEmailRegistered = useRef(false);
  const [modalParameters, setModalParameters] =
    useState<AnonymousBackInStockModalParameters | null>(null);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [emailIsRegistered, setEmailIsRegistered] = useState(false);
  const { getTranslation } = useRuntimeConstants() as {
    getTranslation: (key: string) => string;
  };
  const [loading, setLoading] = useState(false);
  const { saveVariant } = useAnonymousBackInStock()!;
  const { areFeaturesReady } = useFeaturesReady() as {
    areFeaturesReady: boolean;
  };
  const bucket = areFeaturesReady
    ? getFeature("pdp_anonymous_backinstock_enabled")?.getVariables()?.bucket
    : undefined;

  const closeModal = useCallback(() => {
    setEmailIsRegistered(false);
    setModalParameters(null);
    setEmail("");
    setEmailError(false);
    setLoading(false);

    if (modalParameters?.focusOnCloseElement) {
      modalParameters.focusOnCloseElement.focus();
    }
  }, [modalParameters]);

  useEffect(() => {
    if (emailIsRegistered) {
      recognisedHeadingRef.current?.focus();
    } else if (wasEmailRegistered.current && modalParameters) {
      emailInputRef.current?.focus();
    }
    wasEmailRegistered.current = emailIsRegistered;
  }, [emailIsRegistered, modalParameters]);

  useEffect(() => {
    if (emailIsRegistered) {
      fireRegisteredAccountScreenLoadAnalytics(
        modalParameters?.selectedVariantId,
      );
    }
  }, [emailIsRegistered, modalParameters]);

  const handleError = useCallback(() => {
    closeModal();
    getWindow().asos?.eventBus?.emit("notificationBar", {
      type: "error",
      copy: getTranslation("anonymous_back_in_stock_modal_generic_error"),
      refocusId: BACK_IN_STOCK_BUTTON_ID,
    });
    setEmail("");
  }, [getTranslation, closeModal]);

  const setBackInStockSessionStorage = useCallback(() => {
    if (modalParameters?.selectedProductId) {
      setSessionStorageItem(
        BACK_IN_STOCK_SELECTED_PRODUCT,
        modalParameters.selectedProductId,
      );
    }
    if (modalParameters?.selectedVariantId) {
      setSessionStorageItem(
        BACK_IN_STOCK_SELECTED_VARIANT,
        modalParameters.selectedVariantId,
      );
    }
  }, [modalParameters]);

  const trackBisMetric = useCallback(() => {
    if (areFeaturesReady) {
      getFeature("pdp_anonymous_backinstock_enabled")?.isEnabled();
      track("back_in_stock_subscribe");
    }
  }, [areFeaturesReady]);

  const handleAnonymousBackInStock = useCallback(async () => {
    if (!modalParameters?.selectedVariantId) {
      setLoading(false);
      handleError();
      return;
    }

    try {
      const trimmedEmail = email.trim();
      const anonymousId = await getAnonymousId(trimmedEmail);
      await createAnonymousBrazeUser({
        email: trimmedEmail,
        anonymousId,
        awaitAuthResult: true,
      });
      await anonymousAddVariantToNotifications({
        anonymousId,
        variantId: modalParameters.selectedVariantId,
      });
      saveVariant(modalParameters.selectedVariantId);
      closeModal();
      getWindow().asos?.eventBus?.emit("notificationBar", {
        type: "success",
        copy: getTranslation("back_in_stock_confirmation"),
        refocusId: BACK_IN_STOCK_BUTTON_ID,
      });
      fireAnonymousBackInStockSuccessAnalytics(
        modalParameters.selectedVariantId,
      ).catch((analyticsError: Error) =>
        logError(ERROR_TYPE.NonCriticalAppError, {
          action: "anonymousBackInStockAnalytics",
          message: analyticsError.message,
        }),
      );
      trackBisMetric();
    } catch (error) {
      handleError();
      logError(ERROR_TYPE.UserActionFailed, {
        action: "anonymousBackInStock",
        message: (error as Error).message,
      });
    } finally {
      setLoading(false);
    }
  }, [
    email,
    modalParameters,
    getTranslation,
    handleError,
    closeModal,
    saveVariant,
    trackBisMetric,
  ]);

  const handleSubmit = useCallback(async () => {
    try {
      const isAvailable = await checkEmailStatus(email);
      if (!isAvailable) {
        setEmailIsRegistered(true);
        setEmail("");
        setEmailError(false);
        setLoading(false);
      } else {
        await handleAnonymousBackInStock();
      }
    } catch {
      handleError();
      setLoading(false);
    }
  }, [email, handleAnonymousBackInStock, handleError]);

  const backInStockSignIn = useCallback(
    (ctaRefString: string = "anonymous notify me screen") => {
      if (modalParameters?.selectedVariantId) {
        setBackInStockSessionStorage();
        loginRedirect({
          extraQueryParameters: {
            ctaRef: ctaRefString,
          },
        });
      }
    },
    [setBackInStockSessionStorage, modalParameters],
  );

  const handleSubmitWithRedirect = useCallback(async () => {
    try {
      const isAvailable = await checkEmailStatus(email);
      if (!isAvailable) {
        backInStockSignIn(
          "anonymous notify me screen - recognised customer redirect",
        );
      } else {
        await handleAnonymousBackInStock();
      }
    } catch {
      handleError();
      setLoading(false);
    }
  }, [email, handleAnonymousBackInStock, handleError, backInStockSignIn]);

  const handleNotifyClick = useCallback(async () => {
    setLoading(true);
    if (validateEmail(email)) {
      await handleSubmit();
    } else {
      setEmailError(true);
      setLoading(false);
    }
  }, [email, handleSubmit]);

  const handleNotifyClickWithRedirect = useCallback(async () => {
    setLoading(true);
    if (validateEmail(email)) {
      await handleSubmitWithRedirect();
    } else {
      setEmailError(true);
      setLoading(false);
    }
  }, [email, handleSubmitWithRedirect]);

  useEventBus(
    "anonBisModalOpen",
    (eventData: AnonymousBackInStockModalParameters) => {
      setModalParameters(eventData);
    },
    [],
  );

  if (!modalParameters) {
    return null;
  }

  const { focusOnCloseElement } = modalParameters;

  const config = (bucket && VARIANT_CONFIGS[bucket]) || DEFAULT_VARIANT_CONFIG;
  const resolvedHandleNotifyClick =
    config.onEmailRegistered === "redirect"
      ? handleNotifyClickWithRedirect
      : handleNotifyClick;

  return (
    <AnonymousBackInStockVariantModal
      config={config}
      closeModal={closeModal}
      focusOnCloseElement={focusOnCloseElement}
      emailIsRegistered={emailIsRegistered}
      emailInputRef={emailInputRef}
      email={email}
      emailError={emailError}
      loading={loading}
      setEmail={setEmail}
      setEmailError={setEmailError}
      handleNotifyClick={resolvedHandleNotifyClick}
      setEmailIsRegistered={setEmailIsRegistered}
      backInStockSignIn={backInStockSignIn}
      getTranslation={getTranslation}
      recognisedHeadingRef={recognisedHeadingRef}
      selectedVariantId={modalParameters?.selectedVariantId}
    />
  );
};

export default AnonymousBackInStockModal;
